import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { ComingSoon } from "@/components/ComingSoon";
import { EmailCreateRoute } from "@/components/FrameCreateRoutes/EmailCreateRoute";
import { TextTileCreateRoute } from "@/components/FrameCreateRoutes/TextTileCreateRoute";
import {
  getConnectorRoute,
  getConnectorRouteParams,
} from "@/features/Connectors/connectorRoutePaths";
import {
  connectorReturnSearch,
  connectorReturnSearchSchema,
} from "@/features/DragonTiles/connectorTileSelectReturn";
import { resolveConnectorParentTileId } from "@/features/DragonTiles/returnToUtils";
import { isTruePlaintextEmailEnabledForSite } from "@/features/DragonTiles/truePlaintextFlag";
import { CHANNEL_MEDIUMS } from "@/types/dragon-tiles";

const searchSchema = z
  .object({
    tileName: z.string(),
    parentTileId: z.string(),
    innerTileId: z.string(),
    returnTo: z
      .enum([
        "tile",
        "connector-tile",
        "broadcast-v2",
        "creative-sphere",
        "dragon-tiles",
        "marketing-calendar",
      ])
      .optional(),
    // absent = drag-and-drop builder
    editor: z.enum(["dnd", "letter", "true_plaintext", "code"]).optional(),
    // Creative sphere search params (passed through when returnTo is creative-sphere)
    medium: z.enum(CHANNEL_MEDIUMS).optional(),
    dragon_tile: z.string().optional(),
    event: z.string().optional(),
    order_by: z.enum(["create_date", "modify_date"]).optional(),
    order: z.enum(["asc", "desc"]).optional(),
    search: z.string().optional(),
    view: z.enum(["grid", "table"]).optional(),
    preview: z.string().optional(),
    deactivated: z.boolean().optional(),
  })
  .merge(connectorReturnSearchSchema);

export const Route = createFileRoute(
  "/_auth/dragon-tiles/$siteId/new/$variant",
)({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  loader: ({ params }) => {
    const { variant } = params;
    if (variant === "email") {
      void import("@/features/EmailEditor/components/EmailFrameBuilder");
      void import("@/components/HTMLFrameBuilder/HTMLFrameBuilder");
    }
  },
});

function RouteComponent() {
  const { variant, siteId } = Route.useParams();
  const search = Route.useSearch();
  const {
    tileName,
    parentTileId,
    innerTileId,
    returnTo,
    connectorParentTileId,
    selectedEventId,
    editor,
  } = search;
  const navigate = useNavigate();
  const connectorReturn = connectorReturnSearch({
    returnTo: search.returnTo,
    connectorParentTileId: search.connectorParentTileId,
    selectedEventId: search.selectedEventId,
    selectedAudienceId: search.selectedAudienceId,
    openTileSelect: search.openTileSelect,
    tileSelectMedium: search.tileSelectMedium,
    tileSelectInsertAt: search.tileSelectInsertAt,
  });

  const handleCancel = () => {
    const target = returnTo ?? "tile";
    if (target === "broadcast-v2") {
      void navigate({
        to: "/dragon-tiles/$siteId/broadcastV2/new/email",
        params: { siteId },
      });
    } else if (target === "creative-sphere") {
      void navigate({
        to: "/creative-sphere/$siteId",
        params: { siteId },
        search: {
          medium: search.medium,
          dragon_tile: search.dragon_tile,
          event: search.event,
          order_by: search.order_by,
          order: search.order,
          search: search.search,
          view: search.view,
        },
      });
    } else if (target === "dragon-tiles") {
      void navigate({
        to: "/dragon-tiles/$siteId",
        params: { siteId },
      });
    } else if (target === "marketing-calendar") {
      // Back to the plan wizard; it resumes the draft via the plan registry.
      void navigate({
        to: "/marketing-calendar/$siteId/new",
        params: { siteId },
      });
    } else if (target === "connector-tile") {
      const connectorTileId = resolveConnectorParentTileId(
        parentTileId,
        returnTo,
        connectorParentTileId,
      );
      void navigate({
        to: getConnectorRoute(connectorTileId),
        params: getConnectorRouteParams(siteId, connectorTileId),
        search: connectorReturn,
      });
    } else {
      void navigate({
        to: "/dragon-tiles/$siteId/$parentTile",
        params: { siteId, parentTile: parentTileId },
      });
    }
  };

  if (variant === "email") {
    // A true-plaintext link on a site without the flag fails loudly rather
    // than silently opening a different editor.
    if (
      editor === "true_plaintext" &&
      !isTruePlaintextEmailEnabledForSite(siteId)
    ) {
      return <ComingSoon />;
    }
    const editorMode =
      editor === "code"
        ? "html"
        : editor === "letter"
          ? "plaintext"
          : editor === "true_plaintext"
            ? "true_plaintext"
            : "full";
    return (
      <EmailCreateRoute
        siteId={siteId}
        frameName={tileName}
        parentTileId={parentTileId}
        innerTileId={innerTileId}
        connectorParentTileId={connectorParentTileId}
        selectedEventId={selectedEventId}
        connectorTileSelectReturn={connectorReturn}
        returnTo={returnTo}
        onCancel={handleCancel}
        editorMode={editorMode}
      />
    );
  }

  if (variant === "text") {
    return (
      <TextTileCreateRoute
        siteId={siteId}
        frameName={tileName}
        parentTileId={parentTileId}
        innerTileId={innerTileId}
        connectorParentTileId={connectorParentTileId}
        selectedEventId={selectedEventId}
        connectorTileSelectReturn={connectorReturn}
        onCancel={handleCancel}
        returnTo={returnTo}
      />
    );
  }

  return <ComingSoon />;
}
